import ExcelJS from 'exceljs';

/**
 * Column definition for Excel export.
 * Uses an accessor so each page can control exactly what is exported
 * and avoid leaking any sensitive or internal-only data.
 */
export interface ExcelColumn<Row = any> {
  header: string;
  accessor: (row: Row) =>
    | string
    | number
    | boolean
    | Date
    | null
    | undefined;
}

/**
 * Pure helper to transform row objects into a 2D array (AOA) that SheetJS
 * can consume. This is kept separate so we can unit test it without touching
 * the browser download APIs.
 */
export function buildWorksheetData<Row>(
  rows: Row[],
  columns: ExcelColumn<Row>[]
): (string | number | boolean)[][] {
  const headerRow = columns.map((col) => col.header);

  const dataRows = rows.map((row) =>
    columns.map((col) => {
      const raw = col.accessor(row);

      if (raw === null || raw === undefined) return '';

      // Normalise Date objects into ISO date strings to avoid locale issues
      const value: any = raw;
      if (value instanceof Date) {
        return value.toISOString();
      }

      return value as string | number | boolean;
    })
  );

  return [headerRow, ...dataRows];
}

interface ExportToExcelOptions<Row> {
  fileName: string;
  sheetName?: string;
  rows: Row[];
  columns: ExcelColumn<Row>[];
}

/**
 * Trigger a client‑side download of an XLSX file for the provided data.
 *
 * NOTE:
 * - Only export fields you actually show in the UI or that the user is
 *   explicitly allowed to see.
 * - Do not pass raw objects with secrets / internal IDs to the accessor.
 */
export function exportToExcel<Row>({
  fileName,
  sheetName = 'Sheet1',
  rows,
  columns
}: ExportToExcelOptions<Row>): void {
  if (!rows.length) {
    // Nothing to export – silently return to avoid confusing the user.
    return;
  }

  const aoa = buildWorksheetData(rows, columns);
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  aoa.forEach((row) => worksheet.addRow(row));

  void workbook.xlsx.writeBuffer()
    .then((buffer) => {
      const blob = new Blob(
        [buffer],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    })
    .catch(() => {
      // Keep UX non-blocking if file generation fails.
    });
}


